import React, { useEffect, useState } from "react";
import Editor, { OnChange } from "@monaco-editor/react";
import { Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { useQuery } from "@apollo/client";
import { RECIPE_CONTENT } from "../../graphql/AICopilotQueries";

interface RecipeEditorProps {
  recipeName: string;
  theme: "vs-light" | "vs-dark";
  modifiedContent?: string | null;
}

const RecipeEditor = ({
  recipeName,
  theme,
  modifiedContent,
}: RecipeEditorProps) => {
  const [value, setValue] = useState<string>("");

  // GraphQL query to fetch recipe content (now returns JSON)
  const { data, loading, error, refetch } = useQuery(RECIPE_CONTENT, {
    variables: { name: recipeName },
    skip: !recipeName, // Skip query if no recipe is selected
  });

  useEffect(() => {
    // If we have modified content from AI assistant, use that instead of fetched data
    if (modifiedContent) {
      // Modified content is already JSON string, use it directly
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue(modifiedContent);
    } else if (data?.recipeContent) {
      // Backend now returns JSON content directly
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue(data.recipeContent);
    } else if (!loading && !error && recipeName) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue("// Recipe not found");
    } else if (!recipeName) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue("");
    }
  }, [data, recipeName, loading, error, modifiedContent]);

  // Refetch when recipe name changes
  useEffect(() => {
    if (recipeName) {
      refetch();
    }
  }, [recipeName, refetch]);

  const handleEditorChange: OnChange = (value) => {
    setValue(value || "");
  };

  return (
    <Stack height={1}>
      {!recipeName ? (
        <Typography variant="h6" color="text.secondary">
          Select a recipe to edit
        </Typography>
      ) : loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">Failed to load recipe: {error.message}</Alert>
      ) : (
        <Editor
          height="100%"
          defaultLanguage="json"
          theme={theme}
          value={value}
          onChange={handleEditorChange}
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            formatOnPaste: true,
            formatOnType: true,
            wordWrap: "on",
          }}
        />
      )}
    </Stack>
  );
};

export default RecipeEditor;
